// @givmo/compliance — the words and the pure rules of a form signed outside Givmo
// (CCM-R1-SIGN-OUTSIDE-B; the signing design §6.4; frames 84–90, approved as shown, D-CCM-41).
//
// ══ WHERE EVERY LINE COMES FROM ════════════════════════════════════════════════════════════════
//
// APPROVED by Jonathan on 2026-10-07 ("Good" at 12:24 PDT, then "yes" at 13:53 to approving every
// line as shown; the plan folder's `scratchpad/2026-10-07-1353-sign-outside-words-rulings-log.md`,
// on `scratchpad/2026-10-05-cut-1005-7-renders/REVIEW-7-sign-outside-words.html`): W7, W10–W13,
// W15–W17, W19, W22–W27, W29, W30 and "Page {n}". Reused as approved elsewhere: "Title, as your
// organization states it", Save, Cancel, Change, The form, the send sheet's form and version lines,
// the panel's four ledger labels, "someone in your organization". SERVED, and shown as served: the
// statement on a recorded copy (`execution.statement`, W7 as the backend holds it), the signers as
// the organization stated them, and every refusal's `detail`. Nothing here is new.
//
// ══ WHAT A COPY SIGNED OUTSIDE GIVMO IS ═════════════════════════════════════════════════════════
//
// The organization's own record: its upload of the form Givmo prepared, as it was signed on paper
// or somewhere other than Givmo, naming the version it executes, with who signed it, in what
// capacity and when, as the organization states. It is never a Givmo signature, and Givmo never
// checks that it matches the prepared form; every view of it says so. The backend takes one only
// for a form Givmo prepared, only while nothing is out for signature, and never after a Givmo
// signature; a different copy of the same version is recorded beside the first, and the newer
// stands.

import { displayComplianceDate, displayLocalDate } from './compliance-decode';
import { longComplianceDate } from './compliance-panel-model';
import type { DocumentVersion } from './compliance-preparation-decode';
import { SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE } from './compliance-signing-model';

type PersonName = ((userId: string) => string | undefined) | undefined;

// ── The workbench's quiet line (frame 84) ──────────────────────────────────────────────────────

/** W10, the line under the page column's note. */
export const SIGNED_ON_PAPER = 'Signed on paper or outside Givmo?';
/** W11: the line's link, and the sheet's title. */
export const UPLOAD_THE_SIGNED_COPY = 'Upload the signed copy';

// ── The sheet (frames 85, 86) ──────────────────────────────────────────────────────────────────

/** W12, the sheet's lead. */
export const UPLOAD_LEAD =
  'Upload the form as it was signed outside Givmo. Givmo records who signed it and when, as your organization states it.';
/** W26: the sheet's file section, and the recorded copy's card. */
export const THE_SIGNED_COPY = 'The signed copy';
/** W27, the sheet's upload button. */
export const CHOOSE_THE_PDF = 'Choose the PDF';
/** W13: the signers' section and its fields (Title reuses the approved label). */
export const WHO_SIGNED = 'Who signed';
export const SIGNER_NAME = 'Name';
export const DATE_SIGNED = 'Date signed';
export const ADD_ANOTHER_SIGNER = 'Add another signer';
/**
 * W7: the sheet shows it before any copy exists. A recorded copy shows the statement the backend
 * serves on it, which is these words (`EXECUTED_COPY_STATEMENT`, approved there too).
 */
export const SIGNED_OUTSIDE_STATEMENT =
  'Signed outside Givmo. This isn’t a Givmo signature, and Givmo didn’t check that this copy matches the prepared form.';
/** The backend's own limit on how many people one copy names (the request's `signers`, 1–10). */
export const MAX_SIGNERS = 10;
/** The backend's limits on a stated name and title (the signing family's typed-name and title). */
export const SIGNER_NAME_MAX_LENGTH = 100;
export const SIGNER_TITLE_MAX_LENGTH = 120;

// ── The recorded copy (frames 87, 89) ──────────────────────────────────────────────────────────

/** W22: the card's button, and the comparison's title. */
export const COMPARE_WITH_PREPARED = 'Compare with the prepared form';
/** W23, the header's download. */
export const DOWNLOAD_THE_SIGNED_COPY = 'Download the signed copy';
/** W25, the page column's note under the copy's pages. */
export const DOWNLOAD_AS_UPLOADED_NOTE =
  'Download gives you this exact PDF, as your organization uploaded it.';
/** W30, under the form's signature row. */
export const SIGNED_OUTSIDE_ROW_LINE = 'Signed outside Givmo, as your organization states it';

/**
 * W15, the panel's and the card's Signed line: who recorded the copy and the day they did, in the
 * viewer's own calendar (an instant, as every Compliance screen dates one). A recorder the roster no
 * longer names reads as the notices name such a person.
 */
export function signedOutsideLine(
  recorder: string | undefined,
  recordedAt: string | undefined,
): string {
  const who = recorder ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE;
  return `Outside Givmo · recorded by ${who} on ${displayLocalDate(recordedAt)}`;
}

/** W29, the card's subtitle: the day the organization states the copy was last signed. */
export function signedOutsideOnLine(signedOn: string): string {
  return `Signed outside Givmo on ${longComplianceDate(signedOn)}.`;
}

/**
 * W19, one signer as the organization states them: the approved signed line without its time. The
 * date is the civil date the organization gave, read as written.
 */
export function signedByStatedLine(signer: StatedSigner): string {
  return `Signed by ${signer.name} on ${displayComplianceDate(signer.signedOn)}, as ${signer.capacity} (stated by your organization).`;
}

/** The form's signature row for one signer (frame 87): "Daniel Okonkwo · President · Nov 18, 2026". */
export function signedOutsideRowMark(signer: StatedSigner): string {
  return `${signer.name} · ${signer.capacity} · ${displayComplianceDate(signer.signedOn)}`;
}

/** W24, the page column's heading over the copy's own pages. */
export function pageOfSignedCopy(page: number, count: number): string {
  return `Page ${page} of ${count}, the signed copy`;
}

// ── The comparison (frame 88) ──────────────────────────────────────────────────────────────────

/** W16: the comparison's lead and its two headings. */
export const COMPARE_LEAD =
  'Compare the signed copy with the prepared form. Givmo didn’t check that they match.';
export const PREPARED_HEADING = 'Prepared';
export const SIGNED_COPY_HEADING = 'Signed copy';

/** "Page {n}", the comparison's page choice. */
export function pageChoice(page: number): string {
  return `Page ${page}`;
}

/**
 * The pages the comparison offers: every page either side has (a scan with a cover sheet has more
 * than the form), so nothing on either is out of reach. A side with no such page shows an empty
 * frame.
 */
export function comparedPages(prepared: number, copy: number): number[] {
  const count = Math.max(prepared, copy, 0);
  return Array.from({ length: count }, (_, index) => index + 1);
}

// ── A declined request (frame 90) ──────────────────────────────────────────────────────────────

/**
 * W17, under a decline, for the sender: the upload waits for no open request, so the line names the
 * cancel first.
 */
export function declinedPaperLine(name: string | undefined): string {
  return `If ${name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE} would rather sign on paper, cancel this request and upload the copy they sign.`;
}

// ── Reading a recorded copy ────────────────────────────────────────────────────────────────────

/** One person who signed a copy, as the organization states them (the served `signers`). */
export interface StatedSigner {
  name: string;
  capacity: string;
  /** The civil date the organization gave, `YYYY-MM-DD`. */
  signedOn: string;
}

/**
 * The copy signed outside Givmo that stands for a version: the newest `executed_copy` naming it, of
 * the document's versions (oldest first, as the document read serves them). A copy that does not
 * carry its statement and signers is not read as one: it would show no statement beside it.
 */
export function standingCopyOf(
  versions: readonly DocumentVersion[],
  executedVersionId: string | undefined,
): DocumentVersion | undefined {
  if (executedVersionId === undefined) return undefined;
  return versions
    .filter(
      (version) =>
        version.kind === 'executed_copy' &&
        version.derivedFromVersionId === executedVersionId &&
        version.execution !== undefined,
    )
    .at(-1);
}

/** The day the organization states the copy was last signed (the card's W29), or none. */
export function lastSignedOn(copy: DocumentVersion): string | undefined {
  return (copy.execution?.signers ?? [])
    .map((signer) => signer.signedOn)
    .sort()
    .at(-1);
}

/**
 * CCM-R1-SIGN-OUTSIDE-SEND (P4): whether a signing request's completion is the form's signature.
 * The server names the form's execution as its NEWEST version's (`current_execution`), so a
 * request that signed an earlier version — the form prepared again since — is not the form's
 * signature: the form reads unsigned, or as the copy of its newest version signed outside Givmo
 * (D-CCM-53 3 (a): the older signature isn't mentioned). Where the server predates the field
 * (`undefined`), the request's own state reads as it always has.
 *
 * A `null` beside a completed request that signed the form's newest version (`newestVersionId`, one
 * of the request's `signedVersionIds`) is a preparation read from before the request completed: a
 * read since names that request's signed final. So the request stands. The two reads land in either
 * order, and the request's own read is the fresher one once someone signs (cut 1008-15's review, F1).
 */
export function givmoSignatureStands(
  requestState: string | undefined,
  execution: DocumentVersion | null | undefined,
  signedVersionIds: readonly string[] = [],
  newestVersionId?: string,
): boolean {
  if (requestState !== 'completed') return false;
  if (execution === undefined || execution?.kind === 'signed_final') return true;
  return (
    execution === null &&
    newestVersionId !== undefined &&
    signedVersionIds.includes(newestVersionId)
  );
}

/** Who recorded the copy, as the roster names them (W15's `{name}`), or none. */
export function recorderOf(copy: DocumentVersion, personName: PersonName): string | undefined {
  return copy.createdByUserId !== undefined ? personName?.(copy.createdByUserId) : undefined;
}

// ── The sheet's own rules ──────────────────────────────────────────────────────────────────────

/** One row of the sheet's "Who signed" as it is typed. */
export interface SignerRow {
  name: string;
  capacity: string;
  /** `YYYY-MM-DD`, or '' while none is chosen. */
  signedOn: string;
}

export const EMPTY_SIGNER: SignerRow = { name: '', capacity: '', signedOn: '' };

/**
 * Whether Save can act: a PDF chosen and every row filled, each date no later than `today` (the
 * viewer's own `YYYY-MM-DD`). The server takes what the organization states; a date in the future is
 * the one statement the sheet stops before it is sent, by holding Save, with no words of its own.
 */
export function readyToSave(
  fileChosen: boolean,
  rows: readonly SignerRow[],
  today: string,
): boolean {
  return (
    fileChosen &&
    rows.length > 0 &&
    rows.length <= MAX_SIGNERS &&
    rows.every(
      (row) =>
        row.name.trim().length > 0 &&
        row.capacity.trim().length > 0 &&
        /^\d{4}-\d{2}-\d{2}$/.test(row.signedOn) &&
        row.signedOn <= today,
    )
  );
}

/**
 * The act's body: the chosen PDF and each row as typed, trimmed, and the browser's time zone,
 * which the server keeps with the copy for the day a send of it is recorded on, as a request's
 * issue keeps it (CCM-R1-SIGN-OUTSIDE-SEND). With no zone given the key is left out: a server
 * that predates it refuses an unknown field (cut 1008-15's review, F2).
 */
export function executedCopyBody(
  sourceArtifactId: string,
  rows: readonly SignerRow[],
  timeZone?: string,
): ExecutedCopyBody {
  return {
    source_artifact_id: sourceArtifactId,
    signers: rows.map((row) => ({
      name: row.name.trim(),
      capacity: row.capacity.trim(),
      signed_on: row.signedOn,
    })),
    ...(timeZone !== undefined ? { time_zone: timeZone } : {}),
  };
}

/**
 * `POST …/filing-documents/{document_id}/versions/{version_id}/executed-copies`'s body, as the
 * backend's `ComplianceExecutedCopyRecord` takes it (`extra="forbid"`): the vault document holding
 * the signed PDF, and one to ten signers as the organization states them.
 */
export interface ExecutedCopyBody {
  source_artifact_id: string;
  signers: { name: string; capacity: string; signed_on: string }[];
  /** CCM-R1-SIGN-OUTSIDE-SEND: the uploader's IANA time zone, kept with the copy, as at issue. */
  time_zone?: string;
}
